import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import {
  canUseMosPosMenuMasterPage,
  redirect404MosPosMenuMasterPage,
} from '@/lib/AccessControl';
// barrel には jest で失敗する export が含まれているため直接読み込みます
// (src/services/AuthEnterprise/hooks/index.ts の @warning を参照)。
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks/useManagerAccountMe';
import {
  MOS_POS_MENU_MASTER_QUERY_KEY,
  MosPosFailureCode,
  MosPosMenuMasterError,
  MosPosValidateData,
  useImportMosPosMenu,
  useValidateMosPosMenuImport,
} from '@/services/MosPosMenuMaster';

export type ImportPhase =
  | 'idle'
  | 'validating'
  | 'validationFailed'
  | 'previewReady'
  | 'importing'
  | 'importFailed';

export type ImportFailure = MosPosFailureCode | 'NETWORK';

export type ImportPageState = {
  masterFile?: File;
  relationFile?: File;
  validation?: MosPosValidateData;
  phase: ImportPhase;
  errorCode?: ImportFailure;
};

const initialState: ImportPageState = { phase: 'idle' };

/** 一覧へ戻して状態を確認させる必要がある失敗。 */
const REQUIRES_LIST_RELOAD: ImportFailure = 'POS_MENU_IMPORT_RESULT_UNKNOWN';

const LIST_PATH = '/externals/pos-menu-masters';

const toFailure = (error: unknown): ImportFailure => {
  if (error instanceof MosPosMenuMasterError) return error.code;
  return 'NETWORK';
};

export const useMosPosMenuMasterImportPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404MosPosMenuMasterPage(history, managerAccountMe);

  // リクエストを送ってよいかどうかです。上のリダイレクトはアカウントが未解決の間は
  // 何もしないため、それだけではファイル選択や登録の操作を止められません。
  const canUse = canUseMosPosMenuMasterPage(managerAccountMe);

  const { validateImport } = useValidateMosPosMenuImport();
  const { runImport } = useImportMosPosMenu();

  const [state, setState] = useState<ImportPageState>(initialState);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // 検証リクエストの世代番号。応答が返った時点で最新でなければ破棄します。
  // 先に投げた遅い応答が、後から選び直したファイルの結果を上書きするのを防ぎます。
  const generationRef = useRef(0);
  // 送信中の検証を実際に中断するための関数。世代番号は state を守るだけで
  // 通信自体は止まらないため、こちらで回線とサーバ負荷を止めます。
  const abortValidationRef = useRef<(() => void) | undefined>(undefined);
  // 選択中のファイル。setState の updater を純粋に保つため、次の状態の判断は
  // updater の外でこの ref を使って行います。
  const filesRef = useRef<{ master?: File; relation?: File }>({});
  // 二重送信防止。isImporting だけだと同一 tick 内の連打を防げません。
  const importingRef = useRef(false);

  const runValidation = useCallback(
    async (masterFile: File, relationFile: File) => {
      generationRef.current += 1;
      const generation = generationRef.current;
      setState((prev) => ({ ...prev, phase: 'validating' }));

      try {
        const res = await validateImport({
          masterFile,
          relationFile,
          cancelable: {
            onCancel: (abort) => {
              abortValidationRef.current = abort;
            },
          },
        });
        if (generation !== generationRef.current) return;
        setState((prev) => ({
          ...prev,
          validation: res.data,
          phase: res.ok ? 'previewReady' : 'validationFailed',
          errorCode: undefined,
        }));
      } catch (error) {
        if (generation !== generationRef.current) return;
        setState((prev) => ({
          ...prev,
          validation: undefined,
          phase: 'importFailed',
          errorCode: toFailure(error),
        }));
      }
    },
    [validateImport]
  );

  /** ファイルを選び直したら、まず前回の findings と preview を捨てます。 */
  const selectFile = useCallback(
    (kind: 'master' | 'relation', file?: File) => {
      // 画面側でファイル入力を disabled にしているため通常ここは通りませんが、
      // 呼び出し経路が増えても送信されないよう判定を残します。
      if (!canUse) return;

      // 進行中の検証は結果を捨てるだけでなく通信自体も止めます。
      generationRef.current += 1;
      abortValidationRef.current?.();
      abortValidationRef.current = undefined;

      const next = { ...filesRef.current };
      if (kind === 'master') next.master = file;
      else next.relation = file;
      filesRef.current = next;

      // updater は純粋に保ちます。React が updater を複数回呼んでも
      // 副作用が重複しないよう、検証の開始は updater の外で行います。
      setState((prev) => ({
        ...prev,
        masterFile: next.master,
        relationFile: next.relation,
        validation: undefined,
        errorCode: undefined,
        phase: 'idle',
      }));

      // 両方揃ったときだけ自動で検証します
      if (next.master && next.relation) {
        void runValidation(next.master, next.relation);
      }
    },
    [canUse, runValidation]
  );

  const summary = state.validation?.summary;
  const hasBlockingError = (summary?.errors ?? 0) > 0;
  const canRegister =
    canUse && state.phase === 'previewReady' && !hasBlockingError;
  const isImporting = state.phase === 'importing';

  const register = useCallback(async () => {
    if (!canUse) return;
    if (!state.masterFile || !state.relationFile) return;
    if (importingRef.current) return;
    importingRef.current = true;
    setConfirmOpen(false);
    setState((prev) => ({ ...prev, phase: 'importing', errorCode: undefined }));

    try {
      const result = await runImport({
        masterFile: state.masterFile,
        relationFile: state.relationFile,
      });
      // 一覧クエリの invalidate は useImportMosPosMenu の onSuccess が行います。
      // ここでは SCR010 No.23 の成功メッセージを一覧側で出すために件数を渡します。
      // この画面は直後に unmount されるので、自前の state では表示できません。
      history.replace(LIST_PATH, { importResult: result });
    } catch (error) {
      const code = toFailure(error);
      if (
        error instanceof MosPosMenuMasterError &&
        code === 'POS_MENU_IMPORT_VALIDATION_FAILED'
      ) {
        // 再検証で Error が出たので findings を出し直します
        const failed: MosPosMenuMasterError = error;
        setState((prev) => ({
          ...prev,
          validation: failed.validation ?? prev.validation,
          phase: 'validationFailed',
          errorCode: code,
        }));
        return;
      }
      setState((prev) => ({ ...prev, phase: 'importFailed', errorCode: code }));

      if (code === REQUIRES_LIST_RELOAD) {
        // commit の結果が確定していないので、盲目的な再試行をさせません。
        // import フローを閉じ、一覧を読み直させて現在の状態を確認してもらいます。
        queryClient.invalidateQueries(MOS_POS_MENU_MASTER_QUERY_KEY);
        history.replace(LIST_PATH);
      }
    } finally {
      importingRef.current = false;
    }
  }, [
    canUse,
    history,
    queryClient,
    runImport,
    state.masterFile,
    state.relationFile,
  ]);

  return {
    state,
    summary,
    findings: state.validation?.findings ?? [],
    diff: state.validation?.diff,
    previewMenus: state.validation?.preview?.menus ?? [],
    diffByMenu: state.validation?.preview?.diff_types ?? {},
    files: state.validation?.files,
    businessDate: state.validation?.summary.business_date ?? '',
    canUse,
    canRegister,
    isValidating: state.phase === 'validating',
    isImporting,
    confirmOpen,
    openConfirm: () => setConfirmOpen(true),
    closeConfirm: () => setConfirmOpen(false),
    selectFile,
    register,
  };
};
